import { Languages } from "lucide-react";
import { useLanguage, type Lang } from "./language";

/* Each option is written in its own language, so it is findable by someone
   who reads only that one. */
const OPTIONS: readonly { value: Lang; label: string }[] = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिन्दी" },
];

/**
 * The language picker: a two-way segmented control.
 *
 * `tone="panel"` is for the dark brand bands (karigar header, phone sign-in),
 * where the light control would sit on the wrong ground.
 */
export function LanguageSwitch({
  fallback = "en",
  tone = "surface",
  showIcon = false,
}: {
  fallback?: Lang;
  tone?: "surface" | "panel";
  showIcon?: boolean;
}) {
  const { lang, setLang } = useLanguage(fallback);
  const onPanel = tone === "panel";

  return (
    <div className="inline-flex items-center gap-1.5">
      {showIcon ? (
        <Languages aria-hidden="true" className={`size-4 ${onPanel ? "text-panel-muted" : "text-muted"}`} />
      ) : null}
      <div
        aria-label="भाषा / Language"
        className={`inline-flex rounded-md p-0.5 ${onPanel ? "bg-panel-text/10" : "bg-ink/[0.06]"}`}
        role="radiogroup"
      >
        {OPTIONS.map((o) => {
          const active = lang === o.value;
          return (
            <button
              aria-checked={active}
              className={[
                "rounded-[calc(var(--gq-radius-md)-2px)] px-2.5 py-1 font-title text-xs font-semibold transition-colors",
                active
                  ? onPanel
                    ? "bg-panel-text text-panel"
                    : "bg-raised text-ink shadow-[0_1px_2px_rgb(0_0_0/0.08)]"
                  : onPanel
                    ? "text-panel-text/75 hover:text-panel-text"
                    : "text-muted hover:text-ink",
              ].join(" ")}
              key={o.value}
              lang={o.value}
              onClick={() => setLang(o.value)}
              role="radio"
              type="button"
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
